/**
 * Recipe Editor API Response Mappers
 */
import { transformArray, transformKeys } from '@/shared/mappers/base.mapper';

import type { TransformerVersion } from '@/types';

// =============================================================================
// Field Mappings (Backend → Frontend)
// =============================================================================

/**
 * TransformerVersion field mapping for recipe editor
 */
const RECIPE_VERSION_FIELDS = {
  Id: 'id',
  BaseId: 'baseId',
  Name: 'name',
  Version: 'version',
  Config: 'config',
  ConfigJson: 'configJson',
  LanguageVersion: 'languageVersion',
  LastModified: 'lastModified',
  Deployed: 'deployed',
  Comment: 'comment',
  // Additional fields for save
  Environ: 'environ',
  Description: 'description',
  ProcessingType: 'processingType',
  maxDataProcTime: 'maxDataProcTime',
  TemplateName: 'templateName',
  // Preserve camelCase: appId, tenantId
};

// =============================================================================
// Response Transformers (Backend → Frontend)
// =============================================================================

/**
 * Transform GET /transformer/versions response
 * Returns array of transformer versions
 */
export const transformGetRecipeVersionsResponse = (response: unknown): TransformerVersion[] => {
  return transformArray(response, (item) =>
    transformKeys<TransformerVersion>(item, RECIPE_VERSION_FIELDS, {
      parseJson: false, // ConfigJson is already a string
      recursive: true,
    })
  );
};

// =============================================================================
// Request Transformers (Frontend → Backend)
// =============================================================================

/**
 * Transform save recipe request body
 * Matches the legacy API format expected by transformers/create endpoint
 *
 * @param data - Recipe data and metadata
 * @param editorMode - 'visual' or 'yaml' - determines which content field to use
 */
export const transformSaveRecipeRequest = (
  data: {
    baseId: string;
    yamlContent?: string;
    configJson?: string;
    saveMode: string;
    comment?: string;
    versionId?: string;
    name?: string;
    config?: string;
    environ?: string;
    version?: number;
    description?: string;
    languageVersion?: string;
    processingType?: string;
    templateName?: string;
    maxDataProcTime?: string;
  },
  editorMode: 'visual' | 'yaml'
): unknown => {
  // Note: API expects lowercase 'baseid' and 'id', but 'ConfigJson' with PascalCase
  // CRITICAL: baseid and id should be numbers (matches legacy behavior)

  const baseRequestBody = {
    name: data.name || '',
    config:
      editorMode === 'yaml'
        ? data.yamlContent || '' // YAML mode: new YAML content
        : data.config || '', // Visual mode: generated YAML
    environ: data.environ || '',
    version: Number(data.version || 1),
    baseid: Number(data.baseId) || 0, // Number (matches legacy)
    id: Number(data.versionId) || 0, // Number (matches legacy)
    description: data.description || '',
    languageVersion: data.languageVersion || '2.0',
    processingType: data.processingType || 'batch',
    comment: data.comment || '',
    templateName: data.templateName || '',
    maxDataProcTime: data.maxDataProcTime || '00:00:00',
  };

  // CRITICAL: Visual mode includes ConfigJson, YAML mode does NOT (field omitted entirely)
  const requestBody =
    editorMode === 'visual'
      ? {
          ...baseRequestBody,
          ConfigJson: data.configJson || '', // Add ConfigJson for visual mode
        }
      : baseRequestBody; // No ConfigJson field for YAML mode

  return requestBody;
};

/**
 * Transform validate recipe request body
 */
export const transformValidateRecipeRequest = (data: { configJson: string }): unknown => {
  return {
    ConfigJson: data.configJson,
  };
};
